import React, { Component } from "react";
import ShareIconSVG from "../../../Assets/svg/ShareIconSVG.svg";
import SpeakerIconSVG from "../../../Assets/svg/SpeakerIconSVG.svg";
import CopyIconSVG from "../../../Assets/svg/CopyIconSVG.svg";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withRouterHooks } from "../../../withRouters/withRoutersHook";
import Ripple from "../../Animations/Ripple/Ripple";
import {
  ResetIsNewChatSession,
  ClearIndividualChatSession,
} from "../../../Actions/AI";

// Typing effect component
class TypeWriter extends Component {
  static propTypes = {
    text: PropTypes.string.isRequired,
    speed: PropTypes.number,
    onComplete: PropTypes.func,
    processTextForHTML: PropTypes.func
  };

  constructor(props) {
    super(props);
    this.state = {
      displayText: '',
      currentIndex: 0,
      isTyping: true
    };
  }

  componentDidMount() {
    this.startTyping();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.text !== this.props.text) {
      this.setState({
        displayText: '',
        currentIndex: 0,
        isTyping: true
      }, () => {
        this.startTyping();
      });
    }
  }

  startTyping = () => {
    const { text, speed = 20 } = this.props;
    
    // Safety check
    if (!text || typeof text !== 'string') {
      console.warn('TypeWriter: text prop is required and must be a string');
      return;
    }
    
    if (this.typingInterval) {
      clearInterval(this.typingInterval);
    }

    // Simple, reliable typing animation
    this.typingInterval = setInterval(() => {
      const { currentIndex } = this.state;
      
      if (currentIndex < text.length) {
        this.setState(prevState => ({
          displayText: text.slice(0, prevState.currentIndex + 1),
          currentIndex: prevState.currentIndex + 1
        }));
      } else {
        clearInterval(this.typingInterval);
        this.setState({ isTyping: false });
        if (this.props.onComplete) {
          this.props.onComplete();
        }
      }
    }, speed);
  };

  componentWillUnmount() {
    if (this.typingInterval) {
      clearInterval(this.typingInterval);
    }
  }

  render() {
    const { displayText, isTyping } = this.state;
    const { processTextForHTML } = this.props;
    
    return (
      <div>
        <div 
          dangerouslySetInnerHTML={{ 
            __html: processTextForHTML ? processTextForHTML(displayText) : displayText 
          }} 
        />
        {isTyping && <span className="typing-cursor">|</span>}
      </div>
    );
  }
}

class ChatStarted extends Component {
  static propTypes = {
    individualChatSession: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
          .isRequired,
        role: PropTypes.string.isRequired,
        content: PropTypes.string.isRequired,
        timestamp: PropTypes.string.isRequired,
      })
    ),
    isIndividualChatSessionFetchSuccess: PropTypes.bool,
    continueChatSessionId: PropTypes.number,
    ResetIsNewChatSession: PropTypes.func.isRequired,
    ClearIndividualChatSession: PropTypes.func.isRequired,
    individualChatSessionFetchSuccess: PropTypes.bool,
    individualChatSessionFailMessage: PropTypes.string,
    navigate: PropTypes.func.isRequired,
    isClicked: PropTypes.bool,
    isChatStreaming: PropTypes.bool,
    streamingChatMessage: PropTypes.string,
  };

  state = {
    speakingMessageId: null,
    pausedMessageId: null,
    typingMessageIds: new Set(), // Track which messages should have typing effect
    lastMessageCount: 0, // Track the number of messages to detect new ones
  };

  chatEndRef = React.createRef();

  componentDidMount() {
    // Don't clear when a stream is already in flight (first message of a new session).
    // The navigate() + chatInputStreamAction() race means the optimistic message is
    // already in Redux before this mount fires — clearing it would hide the user's message.
    if (!this.props.isClicked) {
      this.props.ClearIndividualChatSession();
    }
    this.props.ResetIsNewChatSession();
  }

  componentDidUpdate(prevProps) {
    const { individualChatSession } = this.props;

    // Typing effect: detect new assistant messages
    if (individualChatSession && Array.isArray(individualChatSession) &&
        prevProps.individualChatSession && Array.isArray(prevProps.individualChatSession)) {
      const newMessages = individualChatSession.filter(msg =>
        !prevProps.individualChatSession.find(prevMsg => prevMsg.id === msg.id)
      );
      newMessages.forEach(msg => {
        if ((msg.role === 'assistant' || msg.role === 'bot') && msg.content && !msg.wasStreamed) {
          this.setState(prevState => ({
            typingMessageIds: new Set([...Array.from(prevState.typingMessageIds), msg.id])
          }));
        }
      });
    }

    // Scroll to bottom when messages change
    if (prevProps.individualChatSession !== individualChatSession && individualChatSession) {
      this.scrollToBottom();
    }

    // Clear typing effects when switching chats
    const currentChatId = this.props.location.pathname.split("/").pop();
    const prevChatId = prevProps.location.pathname.split("/").pop();
    if (currentChatId !== prevChatId) {
      this.setState({ typingMessageIds: new Set() });
    }
  }

  componentWillUnmount() {
    // Stop any ongoing speech
    if (speechSynthesis && speechSynthesis.speaking) {
      speechSynthesis.cancel();
    }
  }

  scrollToBottom = () => {
    if (this.chatEndRef.current) {
      this.chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  handleTypingComplete = (messageId) => {
    this.setState(prevState => {
      const newTypingMessageIds = new Set(prevState.typingMessageIds);
      newTypingMessageIds.delete(messageId);
      return { typingMessageIds: newTypingMessageIds };
    });
  };

  stripMarkdown = (text) => {
    // Remove HTML tags
    text = text.replace(/<[^>]+>/g, ' ');
    // Decode HTML entities
    text = text.replace(/&amp;/g, '&');
    text = text.replace(/&lt;/g, '<');
    text = text.replace(/&gt;/g, '>');
    text = text.replace(/&quot;/g, '"');
    text = text.replace(/&#39;/g, "'");
    text = text.replace(/&nbsp;/g, ' ');
    // Remove code blocks
    text = text.replace(/```[\s\S]*?```/g, '[code block]');
    // Remove inline code
    text = text.replace(/`([^`]+)`/g, '$1');
    // Remove bold/italic (asterisk and underscore forms)
    text = text.replace(/\*\*([^*]+)\*\*/g, '$1');
    text = text.replace(/\*([^*]+)\*/g, '$1');
    text = text.replace(/__([^_]+)__/g, '$1');
    text = text.replace(/_([^_]+)_/g, '$1');
    // Remove strikethrough
    text = text.replace(/~~([^~]+)~~/g, '$1');
    // Remove headers
    text = text.replace(/#{1,6}\s+/g, '');
    // Remove horizontal rules
    text = text.replace(/^[-*_]{3,}\s*$/gm, '');
    // Remove blockquotes
    text = text.replace(/^>\s+/gm, '');
    // Remove list bullets (- item, * item, • item)
    text = text.replace(/^[\s]*[-*•]\s+/gm, '');
    // Remove numbered list markers (1. 2. etc.)
    text = text.replace(/^\s*\d+\.\s+/gm, '');
    // Remove table syntax
    text = text.replace(/\|[-:]+[-|:]*\|/g, '');
    text = text.replace(/\|/g, ' ');
    // Remove markdown links, keep label
    text = text.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
    // Remove bare URLs
    text = text.replace(/https?:\/\/\S+/g, '');
    // Collapse whitespace
    text = text.replace(/\s+/g, ' ').trim();
    return text;
  };

  processTextForHTML = (text) => {
    if (!text || typeof text !== 'string') return '';
    
    // Enhanced HTML code block processing - render HTML directly
    text = text.replace(/```html\s*([\s\S]*?)\s*```/g, (match, htmlContent) => {
      // Clean up the HTML content and ensure all links open in a new tab
      let cleanHTML = htmlContent.trim();
      // Add target="_blank" and rel="noopener noreferrer" to all anchor tags
      cleanHTML = cleanHTML.replace(/<a\s+([^>]*?)>/gi, (aTag, attributes) => {
        // Check if target attribute already exists
        if (/target\s*=/i.test(attributes)) {
          return aTag; // Keep existing target attribute
        }
        // Add target="_blank" and security attributes
        return `<a ${attributes} target="_blank" rel="noopener noreferrer">`;
      });
      return `<div class="html-render-block" style="background-color: #f8fafc;">${cleanHTML}</div>`;
    });
    
    // Process other code blocks with syntax highlighting classes
    text = text.replace(/```javascript\s*([\s\S]*?)\s*```/g, '<pre class="code-block language-javascript"><code>$1</code></pre>');
    text = text.replace(/```css\s*([\s\S]*?)\s*```/g, '<pre class="code-block language-css"><code>$1</code></pre>');
    text = text.replace(/```json\s*([\s\S]*?)\s*```/g, '<pre class="code-block language-json"><code>$1</code></pre>');
    text = text.replace(/```python\s*([\s\S]*?)\s*```/g, '<pre class="code-block language-python"><code>$1</code></pre>');
    text = text.replace(/```(\w+)?\s*([\s\S]*?)\s*```/g, '<pre class="code-block language-$1"><code>$2</code></pre>');
    
    // Convert inline code with styling
    text = text.replace(/`([^`]+)`/g, '<code class="inline-code">$1</code>');
    
    // Convert bold/italic
    text = text.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    text = text.replace(/\*([^*]+)\*/g, '<em>$1</em>');
    
    // Convert headers with proper styling (no extra margins)
    text = text.replace(/#{6}\s+(.+)/g, '<h6 style="font-size: 0.875rem; font-weight: 600; margin: 0;">$1</h6>');
    text = text.replace(/#{5}\s+(.+)/g, '<h5 style="font-size: 1rem; font-weight: 600; margin: 0;">$1</h5>');
    text = text.replace(/#{4}\s+(.+)/g, '<h4 style="font-size: 1.125rem; font-weight: 600; margin: 0;">$1</h4>');
    text = text.replace(/#{3}\s+(.+)/g, '<h3 style="font-size: 1.25rem; font-weight: 600; margin: 0;">$1</h3>');
    text = text.replace(/#{2}\s+(.+)/g, '<h2 style="font-size: 1.5rem; font-weight: 600; margin: 0;">$1</h2>');
    text = text.replace(/#{1}\s+(.+)/g, '<h1 style="font-size: 1.75rem; font-weight: 700; margin: 0;">$1</h1>');
    
    // Convert links with proper styling
    text = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" style="color: #3b82f6; text-decoration: underline;">$1</a>');
    
    // Convert lists (no extra margins)
    text = text.replace(/^\* (.+)$/gm, '<li style="margin-left: 20px; margin-bottom: 0; list-style-type: disc;">$1</li>');
    text = text.replace(/^\d+\. (.+)$/gm, '<li style="margin-left: 20px; margin-bottom: 0; list-style-type: decimal;">$1</li>');

    // Convert line breaks - ultra compact (single newlines become space, double+ become single br)
    text = text.replace(/\n\n+/g, '<br>');
    text = text.replace(/\n/g, ' ');
    
    return text;
  };

  handleTextToSpeech = (messageId, text) => {
    if (this.state.speakingMessageId === messageId || this.state.pausedMessageId === messageId) return;
    speechSynthesis.cancel();
    const cleanText = this.stripMarkdown(text);
    const utterance = new SpeechSynthesisUtterance(cleanText);
    utterance.onend = () => this.setState({ speakingMessageId: null, pausedMessageId: null });
    utterance.onerror = () => this.setState({ speakingMessageId: null, pausedMessageId: null });
    speechSynthesis.speak(utterance);
    this.setState({ speakingMessageId: messageId, pausedMessageId: null });
  };

  handlePauseSpeech = () => {
    if (speechSynthesis.speaking && !speechSynthesis.paused) {
      speechSynthesis.pause();
      this.setState({ pausedMessageId: this.state.speakingMessageId, speakingMessageId: null });
    }
  };

  handleResumeSpeech = () => {
    speechSynthesis.resume();
    this.setState({ speakingMessageId: this.state.pausedMessageId, pausedMessageId: null });
  };

  handleStopSpeech = () => {
    speechSynthesis.cancel();
    this.setState({ speakingMessageId: null, pausedMessageId: null });
  };

  renderMessages() {
    const { individualChatSession, isClicked, isChatStreaming, streamingChatMessage } = this.props;
    const { typingMessageIds } = this.state;

    const typingIndicator = isClicked && !streamingChatMessage ? (
      <div key="typing-indicator" className="flex justify-start">
        <div className="rounded-2xl bg-white px-4 py-3 shadow-sm ring-1 ring-slate-200">
          <div className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "0ms" }} />
            <span className="h-2 w-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "150ms" }} />
            <span className="h-2 w-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "300ms" }} />
          </div>
        </div>
      </div>
    ) : null;

    const streamingBubble = isChatStreaming && streamingChatMessage ? (
      <div key="streaming-bubble" className="flex justify-start">
        <div className="max-w-[85%] rounded-2xl px-4 py-3 shadow-sm bg-white text-slate-700 ring-1 ring-slate-200">
          <div className="text-sm leading-7 bot-message-content">
            <div dangerouslySetInnerHTML={{ __html: this.processTextForHTML(streamingChatMessage) }} />
            <span className="typing-cursor">|</span>
          </div>
        </div>
      </div>
    ) : null;

    if (!individualChatSession || !Array.isArray(individualChatSession)) {
      return typingIndicator ? [typingIndicator] : null;
    }

    const isBot = (msg) => msg.role === "assistant" || msg.role === "bot";

    const messages = individualChatSession.map((message) => {
      const isTemporaryMessage = typeof message.id === "string" && message.id.startsWith("temp_");
      const bot = isBot(message);

      return (
        <div key={message.id} className={`flex ${bot ? "justify-start" : "justify-end"}`}>
          <div
            className={`max-w-[85%] rounded-2xl px-4 py-3 shadow-sm ${
              bot
                ? message.isError
                  ? "bg-red-50 text-red-700 ring-1 ring-red-200"
                  : "bg-white text-slate-700 ring-1 ring-slate-200"
                : `bg-gradient-to-br from-blue-600 to-blue-700${isTemporaryMessage ? " opacity-70" : ""}`
            }`}
          >
            <div className={`text-sm leading-7 ${bot ? 'bot-message-content' : 'text-white'}`}>
              {bot ? (
                message.isError ? (
                  <span>{message.content}</span>
                ) : typingMessageIds.has(message.id) && message.content ? (
                  <TypeWriter
                    text={message.content}
                    speed={20}
                    processTextForHTML={this.processTextForHTML}
                    onComplete={() => this.handleTypingComplete(message.id)}
                  />
                ) : (
                  <div dangerouslySetInnerHTML={{ __html: this.processTextForHTML(message.content || '') }} />
                )
              ) : (
                message.content
              )}
            </div>
            <div className={`mt-2 text-[11px] ${bot ? "text-slate-400" : "text-blue-100"}`}>
              {new Date(message.timestamp).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
            </div>
            {bot && !message.isError && (
              <div className="flex gap-2 items-center mt-2">
                {this.state.speakingMessageId === message.id ? (
                  <>
                    <Ripple size="small" className="w-4 h-4 text-slate-400" />
                    <button onClick={this.handlePauseSpeech} title="Pause" className="text-slate-400 hover:text-slate-600">
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
                    </button>
                    <button onClick={this.handleStopSpeech} title="Stop" className="text-slate-400 hover:text-red-500">
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><rect x="4" y="4" width="16" height="16" rx="2"/></svg>
                    </button>
                  </>
                ) : this.state.pausedMessageId === message.id ? (
                  <>
                    <button onClick={this.handleResumeSpeech} title="Resume" className="text-blue-400 hover:text-blue-600">
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><polygon points="5,3 19,12 5,21"/></svg>
                    </button>
                    <button onClick={this.handleStopSpeech} title="Stop" className="text-slate-400 hover:text-red-500">
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><rect x="4" y="4" width="16" height="16" rx="2"/></svg>
                    </button>
                  </>
                ) : (
                  <button onClick={() => this.handleTextToSpeech(message.id, message.content)} title="Read aloud" className="text-slate-400 hover:text-slate-600">
                    <img src={SpeakerIconSVG} alt="speaker" className="w-4 h-4" />
                  </button>
                )}
                <button onClick={() => navigator.clipboard.writeText(this.stripMarkdown(message.content))} className="text-slate-400 hover:text-slate-600">
                  <img src={CopyIconSVG} alt="copy" className="w-4 h-4" />
                </button>
                <button className="text-slate-400 hover:text-slate-600">
                  <img src={ShareIconSVG} alt="share" className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      );
    });

    if (streamingBubble) {
      messages.push(streamingBubble);
    } else if (typingIndicator) {
      messages.push(typingIndicator);
    }

    return messages;
  }

  render() {
    return (
      <div className="flex w-full h-full bg-white justify-center max-w-[100rem] relative">
        <div className="rounded-lg flex flex-col space-y-4 h-full overflow-y-auto pb-[6rem] md:pb-[10rem] px-4 md:px-8 w-full pt-4">
          {this.renderMessages()}
          {/* Anchor for scrolling to the bottom */}
          <div ref={this.chatEndRef} />
        </div>

        {/* Enhanced CSS for typing animation and code blocks */}
        <style jsx="true">{`
          .typing-dots span {
            display: inline-block;
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background-color: #6b7280;
            margin: 0 2px;
            animation: typing 1.4s infinite ease-in-out both;
          }

          .typing-dots span:nth-child(1) {
            animation-delay: 0s;
          }

          .typing-dots span:nth-child(2) {
            animation-delay: 0.2s;
          }

          .typing-dots span:nth-child(3) {
            animation-delay: 0.4s;
          }

          @keyframes typing {
            0%,
            80%,
            100% {
              transform: scale(0.6);
              opacity: 0.6;
            }
            40% {
              transform: scale(1);
              opacity: 1;
            }
          }

          .typing-cursor {
            display: inline-block;
            color: #374151;
            animation: blink 1s infinite;
            font-weight: bold;
            margin-left: 2px;
          }

          @keyframes blink {
            0%, 50% {
              opacity: 1;
            }
            51%, 100% {
              opacity: 0;
            }
          }

          /* Enhanced code block styling - ultra compact */
          .code-block {
            background-color: #1f2937;
            color: #f9fafb;
            padding: 4px 6px;
            border-radius: 4px;
            margin: 2px 0;
            overflow-x: auto;
            font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
            font-size: 14px;
            line-height: 1.2;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          }

          .code-block code {
            background: none;
            color: inherit;
            padding: 0;
            font-size: inherit;
            white-space: pre-wrap;
            word-break: break-word;
          }

          .inline-code {
            background-color: #f1f5f9;
            color: #1e293b;
            padding: 1px 4px;
            border-radius: 4px;
            font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
            font-size: 0.875em;
            border: 1px solid #e2e8f0;
          }

          .html-render-block {
            background-color: #f8fafc;
            margin: 2px 0;
          }

          /* Improved message content styling - no extra spacing */
          .bot-message-content h1,
          .bot-message-content h2,
          .bot-message-content h3,
          .bot-message-content h4,
          .bot-message-content h5,
          .bot-message-content h6 {
            color: #1f2937;
            font-weight: 600;
            margin: 0;
            padding: 0;
          }

          .bot-message-content strong {
            font-weight: 700;
            color: #1f2937;
          }

          .bot-message-content em {
            font-style: italic;
            color: #4b5563;
          }

          .bot-message-content a {
            color: #3b82f6;
            text-decoration: underline;
            transition: color 0.2s ease;
          }

          .bot-message-content a:hover {
            color: #1d4ed8;
          }

          .bot-message-content li {
            margin: 0;
            padding: 0;
          }

          .bot-message-content ul,
          .bot-message-content ol {
            margin: 0;
            padding: 0;
          }

          /* Ultra compact message styling */
          .bot-message-content {
            line-height: 1.25;
          }

          .bot-message-content p {
            margin: 0;
            padding: 0;
          }

          /* Responsive improvements */
          @media (max-width: 768px) {
            .code-block {
              padding: 4px;
              font-size: 13px;
              margin: 2px 0;
            }

            .bot-message-content {
              line-height: 1.25;
            }
          }
        `}</style>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  individualChatSession: state.ai.individualChatSession,
  isIndividualChatSessionFetchSuccess:
    state.ai.isIndividualChatSessionFetchSuccess,
  continueChatSessionId: state.ai.continueChatSessionId,
  individualChatSessionFetchSuccess: state.ai.individualChatSessionFetchSuccess,
  individualChatSessionFailMessage: state.ai.individualChatSessionFailMessage,
  isClicked: state.ai.isClicked,
  isChatStreaming: state.ai.isChatStreaming,
  streamingChatMessage: state.ai.streamingChatMessage,
});

const mapDispatchToProps = {
  ResetIsNewChatSession,
  ClearIndividualChatSession,
};

const MemoizedChatStarted = React.memo(ChatStarted, (prevProps, nextProps) => {
  const shouldUpdate = (
    prevProps.individualChatSession !== nextProps.individualChatSession ||
    prevProps.isClicked !== nextProps.isClicked ||
    prevProps.isChatStreaming !== nextProps.isChatStreaming ||
    prevProps.streamingChatMessage !== nextProps.streamingChatMessage ||
    prevProps.location.pathname !== nextProps.location.pathname
  );
  return !shouldUpdate;
});

export default withRouterHooks(
  connect(mapStateToProps, mapDispatchToProps)(MemoizedChatStarted)
);
